import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';

// ─── Cache-shape helpers ──────────────────────────────────────────────────────
// supaFetch returns { data: [...] }. TanStack stores this raw envelope in cache
// BEFORE select() unwraps it. Every setQueryData callback therefore receives
// the envelope — not a plain array. These helpers make all optimistic updates safe.
function rawToArray(old) {
  if (Array.isArray(old)) return old;
  if (old && Array.isArray(old.data)) return old.data;
  return [];
}
function patchRaw(old, nextArray) {
  if (Array.isArray(old)) return nextArray;
  return { ...(old || {}), data: nextArray };
}

// ─── useCustomers ─────────────────────────────────────────────────────────────
export function useCustomers() {
  return useQuery({
    queryKey:  queryKeys.admin.customers(),
    queryFn:   () => supaFetch('GET', 'customers', null, '?order=last_order_at.desc.nullslast'),
    select:    (result) => result.data ?? [],
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── useSaveCustomer ──────────────────────────────────────────────────────────
// Upserts a customer. Discriminates insert vs update by presence of created_at:
// new blank rows have no created_at; DB rows always do.
// (Do NOT use a cache lookup to discriminate — onMutate runs before mutationFn,
// so the optimistic record is already in cache when mutationFn executes.)
export function useSaveCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customer) => {
      const isNew = !customer.created_at;
      if (isNew) {
        await supaFetch('POST', 'customers', { ...customer, created_at: new Date().toISOString() });
      } else {
        await supaFetch('PATCH', 'customers', customer, `?id=eq.${customer.id}`);
      }
      return customer;
    },

    onMutate: async (customer) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.customersRoot() });
      const snapshot = queryClient.getQueryData(queryKeys.admin.customers());
      queryClient.setQueryData(queryKeys.admin.customers(), (old) => {
        const arr = rawToArray(old);
        const idx = arr.findIndex(c => c.id === customer.id);
        const next = idx >= 0
          ? arr.map((c, i) => i === idx ? customer : c)
          : [customer, ...arr];
        return patchRaw(old, next);
      });
      return { snapshot };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot !== undefined)
        queryClient.setQueryData(queryKeys.admin.customers(), ctx.snapshot);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.customersRoot() });
    },
  });
}

// ─── useDeleteCustomer ────────────────────────────────────────────────────────
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => supaFetch('DELETE', 'customers', null, `?id=eq.${id}`),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.customersRoot() });
      const snapshot = queryClient.getQueryData(queryKeys.admin.customers());
      queryClient.setQueryData(queryKeys.admin.customers(), (old) =>
        patchRaw(old, rawToArray(old).filter(c => c.id !== id))
      );
      return { snapshot };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot !== undefined)
        queryClient.setQueryData(queryKeys.admin.customers(), ctx.snapshot);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.customersRoot() });
    },
  });
}
